import { Position } from "@xyflow/react";
import type { Edge } from "@xyflow/react";
import type { ResolverActionDto, ResolverActionType, ResolverConfigDto, ResolverEdgeDto, ResolverGraphAction, ResolverCodeType, WhenDto } from "../../../../../types/resolver";
import type { ResolverConfigDtoToGraphResult, ResolverGraphNodeData } from "./types";
import { nodeOrderForLayout, treeLayoutPositions } from "./layout";
import { stripNullsDeep } from "./stripNullsDeep";


/** Map API actionDetails back to UI graph actionDetails shape (inverse of graphActionToDtoDetails). */
export function dtoActionDetailsToGraph(
    details: Record<string, unknown> | undefined,
    actionType: ResolverActionType
): Record<string, unknown> {
    const d = details ?? {};
    switch (actionType) {
        case "SET_DERIVED_ATTRIBUTE":
            return {
                derivedAttributeName: d.derivedAttributeName ?? "RESULT",
                derivedAttributeValue: d.derivedAttributeValue,
                derivedAttributeType: d.derivedAttributeType ?? "STRING",
            };
        case "SET_ANSWER":
            return {
                questionId: d.questionId != null ? String(d.questionId) : "",
                answerSetId: d.answerSetId != null ? String(d.answerSetId) : "",
                answerId: d.answerId != null ? String(d.answerId) : "",
            };
        case "ADD_CODE": {
            const attrs = (d.attributes as Array<{ answerSetId?: unknown; answerId?: unknown; answerValueId?: unknown }>) ?? [];
            const attributes = attrs.map((a, i) => ({
                id: `attr-${i}-${Date.now()}`,
                questionId: 0,
                questionLabel: "",
                answerSetId: a?.answerSetId != null ? a.answerSetId : null,
                answerValueId: a?.answerId ?? a?.answerValueId ?? null,
                answerDisplayText: null,
            }));
            const rawDerived = d.derivedAttributes as Record<string, unknown> | undefined;
            const derivedAttributes =
                rawDerived != null && typeof rawDerived === "object" && !Array.isArray(rawDerived)
                    ? rawDerived
                    : {};
            return {
                code: d.code != null ? String(d.code) : "",
                codeType: (d.codeType as ResolverCodeType) ?? "DIAGNOSIS",
                mark_code_as_ideal: Boolean(d.mark_code_as_ideal),
                attributes,
                derivedAttributes,
            };
        }
        case "THROW_ERROR":
            return {
                message: d.message != null ? String(d.message) : "Resolver validation failed",
                errorCode: d.errorCode != null ? String(d.errorCode) : "",
                severity: d.severity != null ? String(d.severity) : "ERROR",
                field: d.field != null ? String(d.field) : "",
                stopExecution: typeof d.stopExecution === "boolean" ? d.stopExecution : true,
                nodeId: typeof d.nodeId === "number" ? d.nodeId : undefined,
                details: typeof d.details === "object" && d.details !== null ? d.details : undefined,
            };
        case "MARK_CODE_AS_IDEAL":
            return {};
        default:
            return { ...d };
    }
}

/** Convert a single DTO action to graph action (string id, UI-shaped actionDetails). */
export function dtoActionToGraphAction(dto: ResolverActionDto, nodeId: number, actionIndex: number): ResolverGraphAction {
    const actionType = (dto.action as ResolverActionType) ?? "SET_DERIVED_ATTRIBUTE";
    const when = dto.rules?.when;
    const rules =
        when != null
            ? { when: stripNullsDeep(when) as WhenDto }
            : undefined;
    return {
        id: `action-${nodeId}-${actionIndex}`,
        action: actionType,
        actionDetails: dtoActionDetailsToGraph(dto.actionDetails as Record<string, unknown> | undefined, actionType),
        rules,
    };
}


/** Convert a resolver config DTO (e.g. from pasted JSON) to React Flow nodes and edges. */
export function resolverConfigDtoToGraph(config: ResolverConfigDto): ResolverConfigDtoToGraphResult {
    const ordered = nodeOrderForLayout(config);
    const positions = treeLayoutPositions(config);
    const nodes = ordered.map((node, index) => {
        const actions = (node.actions ?? []).map((a, i) => dtoActionToGraphAction(a, node.id, i));
        const pos = positions.get(node.id) ?? { x: 80 + index * 48, y: 60 + index * 36 };
        return {
            id: String(node.id),
            position: pos,
            data: {
                label: node.name ?? `Node ${node.id}`,
                evaluates: node.evaluates ?? "CODES",
                actions,
            },
            type: "resolverNode",
            targetPosition: Position.Left,
            sourcePosition: Position.Right,
        };
    });

    const edgeList: Edge<{ when?: WhenDto }>[] = [];
    const nodeIds = new Set(config.nodes.map((n) => n.id));
    for (const node of config.nodes) {
        const sourceId = String(node.id);
        const edges = (node.edges ?? []) as ResolverEdgeDto[];
        edges.forEach((edge, i) => {
            if (!nodeIds.has(edge.toNodeId)) return;
            const edgeWhen = edge.rules?.when;
            edgeList.push({
                id: `e-${sourceId}-${edge.toNodeId}-${i}`,
                source: sourceId,
                target: String(edge.toNodeId),
                data: edgeWhen != null ? { when: stripNullsDeep(edgeWhen) as WhenDto } : {},
            });
        });
    }

    return { nodes, edges: edgeList };
}
